/**
 * AI Provider Configuration
 * Sends chat requests through the backend proxy so the API key
 * stays server-side and is never exposed to the browser.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const DEFAULT_MODEL = 'deepseek-chat';

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  tool_call_id?: string;
  tool_calls?: ToolCall[];
}

export interface ToolCall {
  id: string;
  type: 'function';
  function: {
    name: string;
    arguments: string;
  };
}

export interface ChatCompletionRequest {
  messages: ChatMessage[];
  model?: string;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
  tools?: ToolDefinition[];
  tool_choice?: 'auto' | 'none' | 'required';
}

export interface ToolDefinition {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: Record<string, unknown>;
  };
}

export interface ChatCompletionResponse {
  id: string;
  choices: Array<{
    index: number;
    message: ChatMessage;
    finish_reason: 'stop' | 'length' | 'tool_calls' | 'error';
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export class AIProvider {
  private model: string;

  constructor() {
    this.model = DEFAULT_MODEL;
  }

  /**
   * Check if the backend API URL is configured.
   * The API key is stored server-side, so we just need the API URL.
   */
  isConfigured(): boolean {
    return !!API_BASE_URL;
  }

  /**
   * Send a chat completion request through the backend proxy.
   * The user's auth token is passed as Bearer token to the proxy,
   * which validates it before forwarding to DeepSeek.
   */
  async chat(
    request: ChatCompletionRequest,
    authToken?: string,
    signal?: AbortSignal
  ): Promise<ChatCompletionResponse> {
    if (!this.isConfigured()) {
      throw new Error('Backend API URL is not configured. Please set REACT_APP_API_URL in your .env file.');
    }

    if (!authToken) {
      throw new Error('Authentication token is required. Please log in.');
    }

    // API_BASE_URL already includes /api/ (e.g., https://api.balancio.in/api/)
    // So append only ai/chat (not /api/ai/chat which would double it)
    const baseUrl = API_BASE_URL.replace(/\/+$/, '');
    const proxyUrl = `${baseUrl}/ai/chat`;

    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        model: this.model,
        messages: request.messages,
        temperature: request.temperature ?? 0.7,
        max_tokens: request.max_tokens ?? 4096,
        stream: false,
        tools: request.tools,
        tool_choice: request.tool_choice ?? 'auto',
      }),
      signal,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      let errorMessage = `AI Chat error (${response.status})`;

      try {
        const errorJson = JSON.parse(errorBody);
        if (errorJson.error?.message) {
          errorMessage = errorJson.error.message;
        } else if (typeof errorJson.error === 'string') {
          errorMessage = errorJson.error;
        }
      } catch {
        errorMessage = errorBody || errorMessage;
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data as ChatCompletionResponse;
  }
}

export const aiProvider = new AIProvider();
